/**
 * Rebuild the lead form's submit control as a theme button.
 *
 * The form arrives pre-rendered from the central forms API, so its markup
 * cannot be filtered in PHP here. Once the forms plugin has injected it, give
 * the control the structure a-button.twig produces, so the existing button
 * styles and rollover apply with no further CSS:
 *
 *   .a-button > .a-button__link > .a-button__link-container > span.default-text
 *                                                           + span.hover-text
 */
const FORM_CONTAINER = '[data-leadform-id]';
const SUBMIT_SELECTOR = 'input[type="submit"].gform_button, button[type="submit"].gform_button';
const TEXT_VARIANTS = ['default-text', 'hover-text'];

const toButton = (input: HTMLInputElement): HTMLButtonElement => {
  const button = document.createElement('button');

  Array.from(input.attributes).forEach(({ name, value }) => {
    // `value` becomes the button's text content instead.
    if (name !== 'value') {
      button.setAttribute(name, value);
    }
  });

  button.type = 'submit';
  input.replaceWith(button);

  return button;
};

const dressSubmitButton = (root: Element) => {
  const control = root.querySelector<HTMLInputElement | HTMLButtonElement>(SUBMIT_SELECTOR);

  // The inner container marks a control this has already rebuilt, which also
  // stops the observer below reacting to our own mutations. The forms plugin
  // can apply a `submit` label default after that by overwriting the button's
  // textContent, which drops the container, so check for it rather than the
  // wrapper and rebuild from the new label when it is gone.
  if (!control || control.querySelector('.a-button__link-container')) {
    return;
  }

  const isInput = control instanceof HTMLInputElement;
  const label = (isInput ? control.value : control.textContent).trim();
  const button = isInput ? toButton(control) : (control as HTMLButtonElement);

  button.textContent = '';
  button.classList.add('a-button__link');

  const container = document.createElement('div');
  container.className = 'a-button__link-container';

  TEXT_VARIANTS.forEach((variant) => {
    const text = document.createElement('span');
    text.className = `a-button__link__text ${variant}`;
    text.textContent = label;
    container.appendChild(text);
  });

  button.appendChild(container);

  if (button.parentElement?.classList.contains('a-button')) {
    return;
  }

  // Wrap in place: the control has to stay inside the form to submit it.
  const wrapper = document.createElement('div');
  wrapper.className = 'a-button is-size-large';
  button.replaceWith(wrapper);
  wrapper.appendChild(button);
};

const watchForForms = () => {
  document.querySelectorAll(FORM_CONTAINER).forEach((container) => {
    new MutationObserver(() => dressSubmitButton(container)).observe(container, {
      childList: true,
      subtree: true,
    });

    // In case the form is already in place.
    dressSubmitButton(container);
  });
};

document.addEventListener('leadFormRendered', (event: Event) => {
  const { form } = (event as CustomEvent<{ form: Element }>).detail;

  if (form) {
    dressSubmitButton(form);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchForForms);
} else {
  watchForForms();
}
